import { ArdoqId } from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import { selectOptionOperations, SelectOptionsOrGroups } from '@ardoq/select';
import { isArdoqError } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';
import { logError } from '@ardoq/logging';
import {
  FetchComponentOptionsWithQuery,
  Fields,
  GetAISuggestions,
} from '../../types';
import {
  prepareAiOptionsgroup,
  prepareOptionsWithLimit,
} from './questionOptionsOperations';

type SearchOptionsArgs = {
  surveyId: string;
  questionId: string;
  componentId: ArdoqId | undefined;
  currentLocale: Locale;
  fields: Fields;
  query: string;
  identifierFieldName: string | undefined;
  shouldFetchReferenceSuggestions: boolean;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  i18nLocale: I18nLocale;
};

export const searchOptions = async ({
  surveyId,
  questionId,
  componentId,
  currentLocale,
  fields,
  query,
  identifierFieldName,
  shouldFetchReferenceSuggestions,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  i18nLocale,
}: SearchOptionsArgs): Promise<SelectOptionsOrGroups<string>> => {
  const [optionsResponse, aiResponse] = await Promise.all([
    fetchComponentOptionsWithQuery({
      surveyId,
      questionId,
      componentId,
      query,
    }),
    shouldFetchReferenceSuggestions && componentId
      ? getAISuggestions(surveyId, questionId, componentId)
      : [],
  ]);
  if (isArdoqError(optionsResponse)) {
    logError(optionsResponse);
    return [
      {
        value: '',
        label: t(
          i18nLocale,
          'surveysUi:options.errorFetching',
          'Error fetching options'
        ),
      },
    ];
  }

  const shouldSort = !query.length;

  const options = prepareOptionsWithLimit(
    optionsResponse,
    identifierFieldName,
    fields,
    currentLocale,
    shouldSort
  );

  if (aiResponse.length === 0) {
    return options;
  }

  const aiOptionsGroup = prepareAiOptionsgroup(
    aiResponse,
    identifierFieldName,
    fields,
    currentLocale,
    i18nLocale
  );

  return selectOptionOperations.prependGroup(options, aiOptionsGroup);
};
